import { z } from 'zod';

/**
 * Feature Flag Type Enum
 * Defines the two types of feature flags supported
 */
export const FlagType = {
  BOOLEAN: 'boolean',
  VARIANT: 'variant',
} as const;

export type FlagTypeValue = (typeof FlagType)[keyof typeof FlagType];

/**
 * Zod schema for flag type validation
 */
const FlagTypeSchema = z.enum([FlagType.BOOLEAN, FlagType.VARIANT]);

/**
 * Feature Flag Registry Schema
 * Validates the structure of the feature flag registry
 */
const FeatureFlagRegistrySchema = z.record(
  z.string().min(1, 'Flag key cannot be empty'),
  FlagTypeSchema,
);

/**
 * FEATURE FLAG REGISTRY
 *
 * This is the single source of truth for all feature flags in the application.
 *
 * Guidelines:
 * - Use kebab-case for flag keys (e.g., 'new-dashboard', 'homepage-layout')
 * - Boolean flags: Simple on/off toggles
 * - Variant flags: Flags with multiple possible values (A/B testing, themes, etc.)
 * - Always add new flags here before using them in code
 * - Document the purpose of each flag with a comment
 *
 * @example
 * ```typescript
 * const FEATURE_FLAGS = {
 *   // UI Features
 *   'new-dashboard': FlagType.BOOLEAN,        // Enable new dashboard redesign
 *   'homepage-layout': FlagType.VARIANT,      // A/B test for homepage layout (classic/modern)
 *
 *   // Experimental Features
 *   'advanced-metrics': FlagType.BOOLEAN,     // Enable advanced analytics
 *   'theme-selector': FlagType.VARIANT,       // Theme selection (light/dark/auto)
 * } as const;
 * ```
 */
export const FEATURE_FLAGS = {
  'ai-module-files': FlagType.VARIANT,
  'announcement-rail': FlagType.VARIANT,
  'bulk-site-onboarding': FlagType.BOOLEAN,
  'insafe-files-per-tenant-buckets': FlagType.BOOLEAN,
  'enrich-export': FlagType.BOOLEAN,
  'not-found': FlagType.BOOLEAN,
  'org-switcher': FlagType.BOOLEAN,
  'pid-assistance': FlagType.BOOLEAN,
  // Real-time notification updates via SSE instead of polling unread counts
  'notification-sse': FlagType.BOOLEAN,
  // Real-time AAE approval widget updates via SSE instead of polling the approval
  'aae-approval-sse': FlagType.BOOLEAN,
} as const;

/**
 * Validate the feature flag registry at module load time
 * This ensures the registry structure is correct
 */
const validationResult = FeatureFlagRegistrySchema.safeParse(FEATURE_FLAGS);
if (!validationResult.success) {
  console.error(
    'Feature flag registry validation failed:',
    validationResult.error,
  );
  throw new Error(
    `Invalid feature flag registry: ${validationResult.error.message}`,
  );
}

/**
 * Type-safe feature flag keys
 * Extracts all valid flag keys from the registry
 */
export type FeatureFlagKey = keyof typeof FEATURE_FLAGS;

/**
 * Cached Set of registered flag keys for O(1) prototype-safe lookups.
 * Using a Set avoids prototype chain traversal issues (e.g., '__proto__', 'constructor')
 * that affect plain object lookups with `in` or bracket access.
 */
const REGISTRY_KEYS = new Set<string>(Object.keys(FEATURE_FLAGS));

/**
 * Get all registered feature flag keys
 */
export const getRegisteredFlagKeys = (): FeatureFlagKey[] => {
  return Object.keys(FEATURE_FLAGS) as FeatureFlagKey[];
};

/**
 * Check if a flag key is registered
 */
export function isFlagKeyRegistered(key: string): key is FeatureFlagKey {
  return REGISTRY_KEYS.has(key);
}

/**
 * Get the type of a feature flag from the registry
 * Returns null if the flag is not registered
 *
 * @param flagKey - The feature flag key to look up
 * @returns The flag type ('boolean' | 'variant') or null if not found
 */
export function getFlagTypeFromRegistry(flagKey: string): FlagTypeValue | null {
  if (!isFlagKeyRegistered(flagKey)) {
    return null;
  }
  return FEATURE_FLAGS[flagKey];
}

/**
 * Type guard to check if a flag is a boolean flag
 */
export function isBooleanFlag(flagKey: string): boolean {
  return getFlagTypeFromRegistry(flagKey) === FlagType.BOOLEAN;
}

/**
 * Type guard to check if a flag is a variant flag
 */
export function isVariantFlag(flagKey: string): boolean {
  return getFlagTypeFromRegistry(flagKey) === FlagType.VARIANT;
}

/**
 * Extract boolean flag keys from the registry
 */
export type BooleanFlagKey = {
  [K in FeatureFlagKey]: (typeof FEATURE_FLAGS)[K] extends typeof FlagType.BOOLEAN
    ? K
    : never;
}[FeatureFlagKey];

/**
 * Extract variant flag keys from the registry
 */
export type VariantFlagKey = {
  [K in FeatureFlagKey]: (typeof FEATURE_FLAGS)[K] extends typeof FlagType.VARIANT
    ? K
    : never;
}[FeatureFlagKey];

/**
 * Get all boolean flag keys from the registry
 * Useful for debugging, admin panels, or filtering flags by type
 *
 * @returns Array of boolean flag keys
 *
 * @example
 * ```typescript
 * const booleanFlags = getBooleanFlagKeys();
 * // ['new-dashboard', 'advanced-metrics']
 * ```
 */
export function getBooleanFlagKeys(): BooleanFlagKey[] {
  return getRegisteredFlagKeys().filter(
    (key) => (FEATURE_FLAGS[key] as FlagTypeValue) === FlagType.BOOLEAN,
  ) as BooleanFlagKey[];
}

/**
 * Get all variant flag keys from the registry
 * Useful for debugging, admin panels, or filtering flags by type
 *
 * @returns Array of variant flag keys
 *
 * @example
 * ```typescript
 * const variantFlags = getVariantFlagKeys();
 * // ['homepage-layout', 'theme-selector']
 * ```
 */
export function getVariantFlagKeys(): VariantFlagKey[] {
  return getRegisteredFlagKeys().filter(
    (key) => (FEATURE_FLAGS[key] as FlagTypeValue) === FlagType.VARIANT,
  ) as VariantFlagKey[];
}

/**
 * Get a summary of the feature flag registry
 * Useful for debugging or displaying registry statistics
 *
 * @returns Object with registry statistics
 *
 * @example
 * ```typescript
 * const summary = getRegistrySummary();
 * // {
 * //   total: 4,
 * //   boolean: 2,
 * //   variant: 2,
 * //   flags: { ... }
 * // }
 * ```
 */
export function getRegistrySummary(): {
  total: number;
  boolean: number;
  variant: number;
  flags: Record<FeatureFlagKey, FlagTypeValue>;
} {
  const booleanFlags = getBooleanFlagKeys();
  const variantFlags = getVariantFlagKeys();

  return {
    total: getRegisteredFlagKeys().length,
    boolean: booleanFlags.length,
    variant: variantFlags.length,
    flags: { ...FEATURE_FLAGS },
  };
}
